import type { DefineTreatmentDefinitionInput } from "@repo/api/schemas";
import type { TreatmentDefinitionDto as TreatmentDefinition } from "@repo/data-store";
import { useMemo } from "react";
import { z } from "zod";
import { useAppForm } from "@/shared/forms/app-form";
import { focusFirstInvalid } from "@/shared/forms/focus-first-invalid";
import {
  type ClinicalDefinitionFormValues,
  clinicalDefinitionFormSchema,
  clinicalDefinitionToDefaults,
  clinicalDefinitionValuesToInput,
  type DefinitionMeta,
  EMPTY_CLINICAL_DEFINITION,
} from "./clinical-definition-form";

export const treatmentDefinitionFormSchema = clinicalDefinitionFormSchema.extend({
  standardObservations: z.array(z.enum(["tolerance"])),
});

export type TreatmentDefinitionFormValues = ClinicalDefinitionFormValues &
  Pick<z.infer<typeof treatmentDefinitionFormSchema>, "standardObservations">;

export const EMPTY_TREATMENT_DEFINITION: TreatmentDefinitionFormValues = {
  ...structuredClone(EMPTY_CLINICAL_DEFINITION),
  standardObservations: ["tolerance"],
};

export function treatmentDefinitionToDefaults(definition?: TreatmentDefinition | null): TreatmentDefinitionFormValues {
  return {
    ...clinicalDefinitionToDefaults(definition, EMPTY_CLINICAL_DEFINITION),
    standardObservations: [...(definition?.standardObservations ?? ["tolerance"])],
  };
}

export function treatmentDefinitionValuesToInput(
  meta: DefinitionMeta,
  values: TreatmentDefinitionFormValues,
): Omit<DefineTreatmentDefinitionInput, "definitionUrl"> {
  return {
    name: meta.name,
    ...(meta.description.trim() ? { description: meta.description } : {}),
    ...clinicalDefinitionValuesToInput(values),
    standardObservations: values.standardObservations,
  };
}

export function useTreatmentDefinitionForm(definition: TreatmentDefinition | null) {
  const defaultValues = useMemo(() => treatmentDefinitionToDefaults(definition), [definition]);
  return useAppForm({
    defaultValues,
    validators: { onDynamic: treatmentDefinitionFormSchema },
    onSubmit: () => {},
    onSubmitInvalid: focusFirstInvalid,
  });
}

export type TreatmentDefinitionForm = ReturnType<typeof useTreatmentDefinitionForm>;
